import { jpath } from 'jsonpath-mapper';
import { ContentType, Component } from 'contensis-core-api';

import MigratortronBaseService from './MigratortronBaseService';

import mapErrorToObject from '~/transformations/mapAppErrorToObject';
import mapMigrateAttributesToContentType from '~/transformations/mapMigrateAttributesToContentType';
import { Logger } from '~/util/logger';
import NotFoundError from '~/util/NotFoundError';
import { MigrateRequest, SimpleMigrationResult } from '~/models/Operations';
import { MigrateStatus } from '~/models/Migration';
import ContensisRepository from '~/repositories/ContensisRepository';

type Results<T> = {
  [projectId: string]: {
    [contentTypeId: string]: { [status: string]: (T | MappedError)[] };
  };
};

type OperationResult = {
  components?: Results<Component>;
  contentTypes?: Results<ContentType>;
};

type ContentTypeResults<T> = {
  get: () => Results<T>;
  push: ([projectId, contentTypeId, status, result]: [
    string,
    string,
    MigrateStatus,
    T | MappedError
  ]) => Results<T>;
};

class SimpleMigrationService extends MigratortronBaseService {
  constructor(payload: MigrateRequest, preview: boolean) {
    super(payload, preview);
  }

  Migrate = async (contentTypeIds: string[], componentIds: string[]) => {
    const result: OperationResult = {};
    const { contentTypes = [], components = [] } =
      (await this.HydrateContensisRepositories([
        ...contentTypeIds,
        ...componentIds,
      ])) || {};

    result.components = await this.MigrateComponents(components);
    result.contentTypes = await this.MigrateContentTypes(contentTypes);
    const response = this.MapResponse(result);

    Logger.info(`Done migrating`);
    return response;
  };

  MigrateComponents = async (components: Component[]) => {
    const { isPreview, targets } = this;
    const results = this.ContentTypeResults<Component>();

    for (const component of components) {
      Logger.info(
        `Migrating component '${component.id}' to ${Object.keys(targets).join(
          ', '
        )}`
      );

      for (const [, contensis] of Object.entries(targets)) {
        const mappedComponent = mapMigrateAttributesToContentType(
          component,
          contensis.project,
          this.payload.readOnlyFields
        ) as Component;

        let postedComponent: Component | undefined = mappedComponent;
        let error;
        let status: MigrateStatus = contensis.context.components.find(
          (c) => c.id === component.id
        )
          ? 'update'
          : 'create';
        if (!isPreview)
          [error, postedComponent, status] =
            await contensis.repo.UpsertComponent(
              contensis.context.resolve.resource(component.id) as Component,
              mappedComponent
            );

        if (error)
          results.push([
            contensis.project.id,
            component.id,
            'error',
            mapErrorToObject(error),
          ]);
        else if (postedComponent)
          results.push([
            contensis.project.id,
            component.id,
            status,
            postedComponent,
          ]);
      }
    }
    Logger.info(`Done migrating components`);
    return results.get();
  };

  MigrateContentTypes = async (contentTypes: ContentType[]) => {
    const { isPreview, targets } = this;
    const results = this.ContentTypeResults<ContentType>();

    for (const contentType of contentTypes) {
      Logger.info(
        `Migrating content type '${contentType.id}' to ${Object.keys(
          targets
        ).join(', ')}`
      );

      for (const [, contensis] of Object.entries(targets)) {
        const mappedContentType = mapMigrateAttributesToContentType(
          contentType,
          contensis.project,
          this.payload.readOnlyFields
        ) as ContentType;

        let postedContentType: ContentType | undefined = mappedContentType;
        let error;

        let status: MigrateStatus = contensis.context.contentTypes.find(
          (c) => c.id === contentType.id
        )
          ? 'update'
          : 'create';
        if (!isPreview)
          [error, postedContentType, status] =
            await contensis.repo.UpsertContentType(
              contensis.context.resolve.resource(contentType.id) as ContentType,
              mappedContentType
            );

        if (error)
          results.push([
            contensis.project.id,
            contentType.id,
            'error',
            mapErrorToObject(error),
          ]);
        else if (postedContentType)
          results.push([
            contensis.project.id,
            contentType.id,
            status,
            postedContentType,
          ]);
      }
    }
    Logger.info(`Done migrating content types`);
    return results.get();
  };

  Delete = async (contentTypeIds?: string[], componentIds?: string[]) => {
    const results = {} as OperationResult;
    await this.GetTargetRepos();
    // Limit concurrency to 1 to avoid 409 Conflict errors when deleting nested components
    this.queue.concurrency = 1;
    results.contentTypes = await this.DeleteContentTypes(contentTypeIds);
    results.components = await this.DeleteComponents(componentIds);

    const response = this.MapResponse(results);
    Logger.info(`Done deleting`);
    return response;
  };

  DeleteContentTypes = async (contentTypeIds?: string[]) => {
    const results = this.ContentTypeResults<ContentType>();
    // If contentTypeIds not specifed, finish
    if (!contentTypeIds) return results.get();

    const { isPreview, targets } = this;

    const context: ContentType[] = [];
    for (const [, target] of Object.entries(targets)) {
      // If empty contentTypeIds array specifed, delete all found contentTypes
      if (contentTypeIds.length === 0) {
        context.push(...target.context.contentTypes);
      } else {
        // Resolve and reassign all input ids
        for (const [idx, contentTypeId] of Object.entries(contentTypeIds)) {
          const contentType = target.context.resolve.contentType(contentTypeId);

          if (contentType) {
            contentTypeIds[Number(idx)] = contentType?.id;
            context.push(contentType);
          }
        }
        // Set the context to the content types we can resolve
        target.context.contentTypes = context;
      }

      for (const contentTypeId of new Set(
        [...(contentTypeIds || []), ...context.map((c) => c.id)].sort()
      )) {
        const contentType = context.find((c) => c.id === contentTypeId);
        const error: AppError | undefined = !contentType
          ? new NotFoundError(
              `Content type '${contentTypeId}' not found in project ${target.project.id}`
            )
          : undefined;

        if (!isPreview && !error) {
          if (contentType)
            void this.queue.add(() =>
              this.DeleteContentType(contentType, target, results)
            );
        } else {
          results.push([
            target.project.id,
            contentTypeId,
            error ? 'error' : 'delete',
            error ? mapErrorToObject(error) : contentType,
          ]);
        }
      }
    }
    await this.queue.onIdle();
    Logger.info(`Done deleting content types`);
    return results.get();
  };

  DeleteContentType = async (
    contentType: ContentType,
    target: ContensisRepository,
    results: ContentTypeResults<ContentType>
  ) => {
    Logger.warning(
      `Deleting content type '${contentType.id}' from ${Object.keys(
        this.targets
      ).join(', ')}`
    );
    const [error] = await target.repo.DeleteContentType(contentType.id);
    if (error)
      results.push([
        target.project.id,
        contentType.id,
        'error',
        mapErrorToObject(error),
      ]);
    else
      results.push([target.project.id, contentType.id, 'delete', contentType]);
  };

  DeleteComponents = async (componentIds?: string[]) => {
    const results = this.ContentTypeResults<Component>();

    // If componentIds not specifed, finish
    if (!componentIds) return results.get();

    const { isPreview, targets } = this;

    const context: Component[] = [];
    for (const [, target] of Object.entries(targets)) {
      // If empty componentIds array specifed, delete all found components
      if (componentIds.length === 0) {
        context.push(...target.context.components);
      } else {
        // Resolve and reassign all input ids
        for (const [idx, componentId] of Object.entries(componentIds)) {
          const component = target.context.resolve.component(componentId);

          if (component) {
            componentIds[Number(idx)] = component?.id;
            context.push(component);
          }
        }
        // Set the context to the content types we can resolve
        target.context.components = context;
      }

      const componentIdsUnique = new Set(
        [...(componentIds || []), ...context.map((c) => c.id)].sort()
      );

      // To prevent 409 Conflict component in use errors we need
      // to separate child components and delete them last
      const deleteComponents: Component[] = [];
      const deleteChildComponents: Component[] = [];

      for (const componentId of componentIdsUnique) {
        const component = context.find((c) => c.id === componentId);
        const error: AppError | undefined = !component
          ? new NotFoundError(
              `Component '${componentId}' not found in project ${target.project.id}`
            )
          : undefined;
        if (isPreview || error) {
          results.push([
            target.project.id,
            componentId,
            error ? 'error' : 'delete',
            error ? mapErrorToObject(error) : component,
          ]);
        } else {
          if (component)
            if ((component as any).isChild)
              deleteChildComponents.push(component);
            else deleteComponents.push(component);
        }
      }
      for (const component of deleteComponents) {
        void this.queue.add(() =>
          this.DeleteComponent(component, target, results)
        );
      }

      // Wait for non-child components to be deleted
      // before start deleting the child components
      await this.queue.onIdle();

      for (const component of deleteChildComponents) {
        void this.queue.add(() =>
          this.DeleteComponent(component, target, results)
        );
      }
    }

    await this.queue.onIdle();

    Logger.info(`Done deleting components`);
    return results.get();
  };

  DeleteComponent = async (
    component: Component,
    target: ContensisRepository,
    results: ContentTypeResults<Component>
  ) => {
    Logger.warning(
      `Deleting component '${component.id}' from ${Object.keys(
        this.targets
      ).join(', ')}`
    );
    const [error] = await target.repo.DeleteComponent(component.id);
    if (error)
      results.push([
        target.project.id,
        component.id,
        'error',
        mapErrorToObject(error),
      ]);
    else results.push([target.project.id, component.id, 'delete', component]);
  };

  MapResponse = (results: OperationResult) => {
    const getStatusCountsByEntityId = (entities?: Results<any>) =>
      Object.keys(entities || {}).length === 0
        ? undefined
        : Object.fromEntries(
            Object.entries(entities || {}).map(
              ([projectId, entitiesByProject]) => [
                projectId,
                Object.fromEntries(
                  Object.entries(entitiesByProject || {}).map(
                    ([contentTypeId, resultsByStatus]) => [
                      contentTypeId,
                      Object.keys(resultsByStatus || {})?.[0],
                    ]
                  )
                ),
              ]
            )
          );

    const errors = [
      ...((jpath('contentTypes.*..error[*]', results) as MappedError[]) || []),
      ...((jpath('components.*..error[*]', results) as MappedError[]) || []),
    ];
    return {
      committed: !this.isPreview,
      contentTypes: getStatusCountsByEntityId(results.contentTypes),
      components: getStatusCountsByEntityId(results.components),
      errors: errors.length ? errors : undefined,
    } as SimpleMigrationResult;
  };

  ContentTypeResults = <T extends ContentType | Component>() => {
    const results: Results<T> = {};

    return {
      get: () => results,
      push: ([projectId, contentTypeId, status, result]: [
        string,
        string,
        MigrateStatus,
        T | MappedError | undefined
      ]) => {
        if (typeof results[projectId] === 'undefined') results[projectId] = {};
        if (typeof results[projectId][contentTypeId] === 'undefined')
          results[projectId][contentTypeId] = {};
        if (!Array.isArray(results[projectId][contentTypeId][status]))
          results[projectId][contentTypeId][status] = [];
        if (result) results[projectId][contentTypeId][status].push(result);
        return results;
      },
    };
  };
}

export default SimpleMigrationService;
